// src/store/slices/transactionsSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as con from "../../GlobalConstants";
import { DB } from "../logic/databaseLogic";
import { createError, createNotification } from "../../templates/notifications/Notifications";
import { getRequestConfig, getVisualizedUser } from "../logic/supportFunctions";
import axios from "axios";
import { getTotalTransactionPages } from "../../utils/transactionFunctions";
import { parseDate } from "../../utils/dateFunctions";
import { selectCurrentDateISO } from "../selectors/applicationSelectors";
import { createFromCalculatorAnnotation } from "../logic/annotation";
import { extractErrorMessage } from "../../utils/errorFunctions";

// =========================
// Helpers
// =========================
const mapArrayToObject = (arr) =>
  Object.fromEntries(arr.map((x) => [x[con.ID], x]));

const initialTransactionsParams = (param) =>
  Object.fromEntries(con.TRANSACTION_TYPES.map(type => [type, param])
)

// =========================
// Initial State
// =========================
const initialState = {
  byType: {},          // transactions
  annotationsByType: {},  // annotations
  [con.TRANSACTIONS_LOADED] : initialTransactionsParams(con.NOT_STARTED), // Object to track the loading status of the transactions from the server 
  annotationsStatus: initialTransactionsParams(con.NOT_STARTED),  // annotations status  
  [con.MARKET_ALERTS]: [],
  market_alerts_status: con.NOT_STARTED,
  [con.SELECTED_TRANSACTIONS]: con.EXPOSURES,
  [con.ADDING_NEW_ROW] : false, // Variable indicating if the use is adding a new row.
  [con.LOADING_CURRENT_TRANSACTIONS] : false, // Parameter indicting if the interface is loading the current transactions (used for smoother transitions)
  [con.INCLUDE_ACTIVE]: true, // Variable to track option to show or hide active transactions.
  [con.INCLUDE_EXPIRED] : true, // Variable to track option to show or hide expired transactions.
  [con.INCLUDE_PREPAY] : true, // Variable to track option to show or hide pre payed transactions.
  [con.INCLUDE_PURCHASES] : true, // Variable to track option to show or hide purchases transactions.
  [con.INCLUDE_SALES] : true, // Variable to track option to show or hide sales transactions.
  [con.CAN_ADD_TRANSACTION] : true, // Variable indicating if the transaction type (Exposure, FWD Coverage, Account etc...) allows adding new rows.
  [con.UPLOADED_FILE] : null, // Variable to store the uploaded transactions file
  [con.UPLOADING_FILE] : false, // Variable to track if the user is currently uploading the transactions file.
  [con.CURRENT_TRANSACTION_NAVIGATION_PAGE]: initialTransactionsParams(0), // Object for handling navigation pages when the user has too many transactions
  [con.TOTAL_TRANSACTIONS_DISPLAYED] : initialTransactionsParams(0), // Object for handling the total number of transactions the user has
  [con.INTERNAL_TRANSACTION_CHANGE] : 0, // Parameter checking the when the user changed attributes of a transactions. It is used for usability and smoother transitions in the interface.
  [con.TRANSACTION_PREPAYED]: con.NOT_STARTED, // Status for prepaying an exposure through the server
  [con.TRANSACTIONS_INSERTED]: initialTransactionsParams(con.OK),  //Object to track insertion status of the transactions (from a user's uploaded file)
  [con.SEARCH_FUN_PARAMS]: null,
  [con.FILTER_FUN_PARAMS]: {
      [con.INCLUDE_EXPIRED]: true,
      [con.INCLUDE_ACTIVE]: true,
      [con.INCLUDE_PREPAY]: true,
      [con.INCLUDE_PURCHASES]: true,
      [con.INCLUDE_SALES]: true
    }

};

// =========================
// THUNKS
// =========================

// 🔹 Fetch Transactions
export const fetchTransactions = createAsyncThunk(
  "transactions/fetch",
  async (transactionType, { rejectWithValue, getState }) => {
    try {
      let res = await DB.fetchTransactions(transactionType);
      const currentDate = selectCurrentDateISO(getState())
      return {
        type: transactionType,
        data: mapArrayToObject(res || []),
        currentDate
      };
    } catch (err) {
      return rejectWithValue({ type: transactionType, error: extractErrorMessage(err, "Error cargando transacciones"),});
    }
  }
);

// 🔹 Fetch Annotations
export const fetchAnnotations = createAsyncThunk(
  "transactions/fetchAnnotations",
  async (transactionType, { rejectWithValue }) => {
    try {
      const res = await DB.fetchAnnotations(transactionType);

      return {
        type: transactionType,
        data: res,
      };
    } catch (err) {
      return rejectWithValue({ type: transactionType, error: extractErrorMessage(err, "Error cargando anotaciones"),});
    }
  }
);

// 🔹 Fetch BOTH (Transactions and annotations)
export const fetchAllLineData = createAsyncThunk(
  "transactions/fetchAllLineData",
  async (_, { dispatch }) => {
    const promises = []

    con.TRANSACTION_TYPES.forEach(type => {
      promises.push(dispatch(fetchTransactions(type)))
      promises.push(dispatch(fetchAnnotations(type)))
    })

    await Promise.all(promises)

    return true;
  }
);

// 🔹 Add Transaction
export const addTransaction = createAsyncThunk(
  "transactions/add",
  async ({ transactionType, values }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const user =
        state[con.REDUCER_AUTHENTICATION][con.SURROGATE_USER]
        ?? state[con.REDUCER_AUTHENTICATION][con.USER]
      const line =
        state[con.REDUCER_APPLICATION_PARAMETERS][con.SELECTED_LINE]
      // Sets the default values and userId
      let payload = {
                ...values, 
                [con.USER] : user[con.ID],
                [con.USER_LINE] : line
               }
      
      const res = await DB.addTransaction(transactionType, payload);
      const transaction = res[con.TRANSACTION] || res;

      return {
        type: transactionType,
        transaction,
      }
    } catch (err) {
      return rejectWithValue({ type: transactionType, error: extractErrorMessage(err, "Error creando la transacción"),});
    }
  }
);

export const bulkAddTransactions = createAsyncThunk(
  "transactions/bulkAdd",
  async ({ transactionType, values }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const user =
        state[con.REDUCER_AUTHENTICATION][con.SURROGATE_USER]
        ?? state[con.REDUCER_AUTHENTICATION][con.USER]

      const payload = values.map(v => ({
        ...v,
        [con.USER]: user[con.ID]
      }))

      const res = await DB.addBulkTransactions(transactionType, payload);

      return {
        type: transactionType,
        data: mapArrayToObject(res)
      };
    } catch (err) {
      return rejectWithValue({ type: transactionType, error: extractErrorMessage(err, "Error cargando transacciones masivas"),});
    }
  }
);

// 🔹 Update Transaction
export const updateTransaction = createAsyncThunk(
  "transactions/update",
  async ({ transactionType, id, values }, { rejectWithValue }) => {
    try {
      const res = await DB.setTransaction(transactionType, id, values);
      const transaction = res[con.TRANSACTION]
      const annotation = res[con.ANNOTATION]
      return {
        type: transactionType,
        transaction,
        annotation,
      };
    } catch (err) {
      return rejectWithValue({ type: transactionType, error: extractErrorMessage(err, "Error actualizando la transacción"),});
    }
  }
);

// 🔹 Delete Transaction
export const deleteTransaction = createAsyncThunk(
  "transactions/delete",
  async ({ transactionType, id }, { rejectWithValue }) => {
    try {
      await DB.deleteTransaction(transactionType, id);

      createNotification(
        "Transacción eliminada correctamente",
        con.NOTIFICATION_TIME_SHORT
      );

      return { type: transactionType, id };
    } catch (err) {
      return rejectWithValue({ type: transactionType, error: extractErrorMessage(err, "Error eliminando la transacción"),});
    }
  }
);

// 🔹 Replace All
export const replaceTransactions = createAsyncThunk(
  "transactions/replaceAll",
  async ({ transactionType, values }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const user =
        state[con.REDUCER_AUTHENTICATION][con.SURROGATE_USER]
        ?? state[con.REDUCER_AUTHENTICATION][con.USER]

      const vals = values.map(v => ({
        ...v,
        [con.USER]: user[con.ID]
      }))
      const res = await DB.replaceTransactions(transactionType, vals);
      return {
        type: transactionType,
        data: mapArrayToObject(res),
      };
    } catch (err) {
      return rejectWithValue({ type: transactionType, error: extractErrorMessage(err, "Error reemplazando transacciones"), });
    }
  }
);

// 🔹 Delete All
export const deleteAllTransactions = createAsyncThunk(
  "transactions/deleteAll",
  async (transactionType, { rejectWithValue }) => {
    try {
      await DB.deleteAllTransactions(transactionType);
      return { type: transactionType };
    } catch (err) {
      return rejectWithValue({ type: transactionType, error: extractErrorMessage(err, "Error eliminando transacciones"), });
    }
  }
);

// FETCH
export const fetchMarketAlerts = createAsyncThunk(
  "transactions/fetchMarketAlerts",
  async (_, { rejectWithValue }) => {
    try {
      
      let user = getVisualizedUser();
      user = user[con.ID]
      const config = getRequestConfig()
      const url = `${con.rest_base_url}${con.transactions_api}${con.usdcop_alerts_null_reached}${user}/`

      const res = await axios.get(url, config);
      
      return res.data;
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error cargando alertas de mercado"));
    }
  }
);

// Prepay Exposure
export const prepayExposure = createAsyncThunk(
  "transactions/prepayExposure",
  async ({ expId, amountToPrepay, paymentMethod, prepaymentRate }, { rejectWithValue }) => {
    try {
      const data = await DB.prepayExposure(expId, amountToPrepay, paymentMethod, prepaymentRate)
      createNotification("Exposición prepagada correctamente", con.NOTIFICATION_TIME_SHORT)

      return data
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error prepagando la exposición"))
    }
  }
)

// Add FWD Coverage from Calculator
export const addFWDFromCalculator = createAsyncThunk(
  "transactions/addFWDFromCalculator",
  async ({ values }, { rejectWithValue, dispatch }) => {
    try {
      const result = await dispatch(addTransaction({ 
        transactionType: con.COVERAGES_FWD, 
        values 
      })).unwrap();

      const transactionID = result.transaction[con.ID];

      const annotation = createFromCalculatorAnnotation(transactionID)

      const createdAnnotation = await DB.addAnnotation(con.COVERAGES_FWD, transactionID, annotation);

      return { transaction: transactionID, annotation: createdAnnotation };
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err));
    }
  }
);

// Add Alert
export const addAlert = createAsyncThunk(
  "transactions/addAlert",
    async (payload, { rejectWithValue }) => {
    try {
      const config = getRequestConfig()

      const response = await axios.post(
        `${con.rest_base_url}${con.transactions_api}${con.usdcop_market_alerts}`,
        payload,
        config
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error creando la alerta"));
    }
  }
);

// Delete Alert
export const deleteAlert = createAsyncThunk(
  "transactions/deleteAlert",
  async (alertId, { rejectWithValue }) => {
    try {
      const config = getRequestConfig()
      await axios.delete(
        `${con.rest_base_url}${con.transactions_api}${con.usdcop_market_alerts}${alertId}/`,
        config
      );
      return alertId;

    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error eliminando la alerta"));
    }
  }
);


// =========================
// SLICE
// =========================

const transactionsSlice = createSlice({
  name: con.REDUCER_TRANSACTIONS,
  initialState,
  reducers: {
    resetTransactions: () => ({ ...initialState }),
    setTransactions: (state, action) => {
      const { id, value } = action.payload;
      state[id] = value;
    },
    firstPage: (state, action) => {
      const transactionType = action.payload
      state[con.CURRENT_TRANSACTION_NAVIGATION_PAGE][transactionType] = 0
    },
    previousPage: (state, action) => {
      const transactionType = action.payload
      const currentPage =
        state[con.CURRENT_TRANSACTION_NAVIGATION_PAGE][transactionType]
      const newPage = Math.max(currentPage - 1, 0)
      state[con.CURRENT_TRANSACTION_NAVIGATION_PAGE][transactionType] = newPage
    },
    nextPage: (state, action) => {
      const transactionType = action.payload
      const currentPage =
        state[con.CURRENT_TRANSACTION_NAVIGATION_PAGE][transactionType]
      const maxPage = getTotalTransactionPages(
        state[con.TOTAL_TRANSACTIONS_DISPLAYED][transactionType]
      )
      const newPage = Math.min(currentPage + 1, maxPage)
      state[con.CURRENT_TRANSACTION_NAVIGATION_PAGE][transactionType] = newPage
    },
    finalPage: (state, action) => {
      const transactionType = action.payload
      const maxPage = getTotalTransactionPages(
        state[con.TOTAL_TRANSACTIONS_DISPLAYED][transactionType]
      )
      state[con.CURRENT_TRANSACTION_NAVIGATION_PAGE][transactionType] = maxPage
    },
    setTotalTransactionsDisplayed: (state, action) => {
      const { type, TOTAL } = action.payload
      state[con.TOTAL_TRANSACTIONS_DISPLAYED][type] = TOTAL
      const maxPage = getTotalTransactionPages(TOTAL)
      state[con.CURRENT_TRANSACTION_NAVIGATION_PAGE][type] =
        Math.min(
          state[con.CURRENT_TRANSACTION_NAVIGATION_PAGE][type],
          maxPage
        )
    }

  },
  extraReducers: (builder) => {
    builder

      // ================= FETCH =================
      .addCase(fetchTransactions.pending, (state, action) => {
        const type = action.meta.arg;
        state[con.TRANSACTIONS_LOADED][type] = con.LOADING;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        const { type, data, currentDate } = action.payload;
        
        // --- Expiration logic ---
        if (con.EXPIRING_TRANSACTION_TYPES.includes(type)) {
          Object.values(data).forEach((t) => {
            if (t[con.STATE] !== con.PREPAYED) {
              t[con.STATE] =
                parseDate(t[con.EXPIRATION_DATE]) > parseDate(currentDate)
                  ? con.ACTIVE
                  : con.EXPIRED
            }
          })
        }
        // --- Store transactions ---
        state.byType[type] = data;
        state[con.TRANSACTIONS_LOADED][type] = con.OK;
        // --- Has exposures flag ---
        if (type === con.EXPOSURES) {
          state.hasTransactions = Object.keys(data).length > 0
        }
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        const { type, error } = action.payload;
        state[con.TRANSACTIONS_LOADED][type] = con.ERROR;
        createError(error);
      })

      // ================= FETCH ANNOTATIONS =================
      .addCase(fetchAnnotations.pending, (state, action) => {
        const type = action.meta.arg;
        state.annotationsStatus[type] = con.LOADING;
      })
      .addCase(fetchAnnotations.fulfilled, (state, action) => {
        const { type, data } = action.payload;
          const grouped = {}
          Object.values(data).forEach((an) => {
              const transId = an[con.TRANSACTION]
              if (!grouped[transId]) grouped[transId] = []
              grouped[transId].push(an)
          })
          state.annotationsByType[type] = grouped;
        state.annotationsStatus[type] = con.OK;
      })
      .addCase(fetchAnnotations.rejected, (state, action) => {
        const { type, error } = action.payload;
        state.annotationsStatus[type] = con.ERROR;
        createError(error);
      })

      // ================= ADD =================
      .addCase(addTransaction.pending, (state, action) => {
        const type = action.meta.arg.transactionType
        state[con.TRANSACTIONS_INSERTED][type] = con.LOADING
      })
      .addCase(addTransaction.fulfilled, (state, action) => {
        const { type, transaction } = action.payload;
        if (!state.byType[type]) state.byType[type] = {};
        state.byType[type][transaction[con.ID]] = transaction;
        state[con.TRANSACTIONS_INSERTED][type] = con.OK
        createNotification("Transacción creada correctamente", con.NOTIFICATION_TIME_SHORT);
      })
      .addCase(addTransaction.rejected, (state, action) => {
        const type = action.payload?.type ?? action.meta.arg.transactionType
        state[con.TRANSACTIONS_INSERTED][type] = con.ERROR
        createError(action.payload?.error);
      })

      .addCase(bulkAddTransactions.pending, (state, action) => {
        const type = action.meta.arg.transactionType
        state[con.TRANSACTIONS_INSERTED][type] = con.LOADING
      })
      .addCase(bulkAddTransactions.fulfilled, (state, action) => {
        const { type, data } = action.payload;
        if (!state.byType[type]) state.byType[type] = {};
        Object.assign(state.byType[type], data);
        createNotification("Transacciones cargadas correctamente", con.NOTIFICATION_TIME_SHORT);
      })
      .addCase(bulkAddTransactions.rejected, (state, action) => {
        const type = action.payload?.type ?? action.meta.arg.transactionType
        state[con.TRANSACTIONS_INSERTED][type] = con.ERROR
        createError(action.payload?.error);
      })

      .addCase(addFWDFromCalculator.fulfilled, (state, action) => {
        const { transaction, annotation } = action.payload;
        if (!state.annotationsByType[con.COVERAGES_FWD]) state.annotationsByType[con.COVERAGES_FWD] = {}
        if (!state.annotationsByType[con.COVERAGES_FWD][transaction])
          state.annotationsByType[con.COVERAGES_FWD][transaction] = []
        state.annotationsByType[con.COVERAGES_FWD][transaction].push(annotation)
      })
      .addCase(addFWDFromCalculator.rejected, (state, action) => {
        createError(action.payload);
      })

      // ================= UPDATE =================
      .addCase(updateTransaction.fulfilled, (state, action) => {
        const { type, transaction } = action.payload;

        if (!state.byType[type]) return;

        state.byType[type][transaction[con.ID]] = transaction;
      })

      .addCase(updateTransaction.pending, (state, action) => {})

      .addCase(updateTransaction.rejected, (state, action) => {
        createError(action.payload?.error);
      })

      // ================= DELETE =================
      .addCase(deleteTransaction.fulfilled, (state, action) => {
        const { type, id } = action.payload;
        if (state.byType[type]) delete state.byType[type][id];
        createNotification("Transacción eliminada correctamente", con.NOTIFICATION_TIME_SHORT);
      })
      .addCase(deleteTransaction.rejected, (state, action) => {
        createError(action.payload?.error);
      })

      // ================= REPLACE =================
      .addCase(replaceTransactions.fulfilled, (state, action) => {
        const { type, data } = action.payload;
        state.byType[type] = data;
      })
      .addCase(replaceTransactions.rejected, (state, action) => {
        createError(action.payload?.error);
      })

      // ================= DELETE ALL =================
      .addCase(deleteAllTransactions.fulfilled, (state, action) => {
        const { type } = action.payload;
        state.byType[type] = {};
        createNotification("Transacciones eliminadas correctamente", con.NOTIFICATION_TIME_SHORT);
      })
      .addCase(deleteAllTransactions.rejected, (state, action) => {
        createError(action.payload?.error);
      })

      // ================= PREPAY EXPOSURE =================
      .addCase(prepayExposure.pending, (state) => {
        state[con.TRANSACTION_PREPAYED] = con.LOADING
      })
      .addCase(prepayExposure.fulfilled, (state, action) => {
        const data = action.payload

        // Update Exposure
        state.byType[con.EXPOSURES][data[con.TRANSACTION][con.ID]] = data[con.TRANSACTION]

        // Exposure Annotation
        if (!state.annotationsByType[con.EXPOSURES]) state.annotationsByType[con.EXPOSURES] = {}
        if (!state.annotationsByType[con.EXPOSURES][data[con.TRANSACTION][con.ID]])
          state.annotationsByType[con.EXPOSURES][data[con.TRANSACTION][con.ID]] = []
        state.annotationsByType[con.EXPOSURES][data[con.TRANSACTION][con.ID]].push(data[con.ANNOTATION])

        // OPTIONAL: SPOT COVERAGE + ANNOTATION
        if (con.SPOT_COVERAGE in data) {
          if (!state.byType[con.COVERAGES_SPOT]) state.byType[con.COVERAGES_SPOT] = {}
          state.byType[con.COVERAGES_SPOT][data[con.SPOT_COVERAGE][con.ID]] = data[con.SPOT_COVERAGE]

          if (!state.annotationsByType[con.COVERAGES_SPOT]) state.annotationsByType[con.COVERAGES_SPOT] = {}
          if (!state.annotationsByType[con.COVERAGES_SPOT][data[con.COVERAGES_SPOT][con.ID]])
            state.annotationsByType[con.COVERAGES_SPOT][data[con.COVERAGES_SPOT][con.ID]] = []
          state.annotationsByType[con.COVERAGES_SPOT][data[con.COVERAGES_SPOT][con.ID]].push(data[con.SPOT_COVERAGES_ANNOTATION])
        }
        

        // OPTIONAL: ACCOUNT + ANNOTATION
        if (con.COMPENSATION_ACCOUNT in data) {
          if (!state.byType[con.ACCOUNTS]) state.byType[con.ACCOUNTS] = {}
          state.byType[con.ACCOUNTS][data[con.COMPENSATION_ACCOUNT][con.ID]] = data[con.COMPENSATION_ACCOUNT]

          if (!state.annotationsByType[con.ACCOUNTS]) state.annotationsByType[con.ACCOUNTS] = {}
          if (!state.annotationsByType[con.ACCOUNTS][data[con.COMPENSATION_ACCOUNT][con.ID]])
            state.annotationsByType[con.ACCOUNTS][data[con.COMPENSATION_ACCOUNT][con.ID]] = []
          state.annotationsByType[con.ACCOUNTS][data[con.COMPENSATION_ACCOUNT][con.ID]].push(data[con.ACCOUNT_ANNOTATION])
        }

        // PREPAY EXPOSURE + ANNOTATION
        if (con.PREPAY_EXPOSURE in data) {
          state.byType[con.EXPOSURES][data[con.PREPAY_EXPOSURE][con.ID]] = data[con.PREPAY_EXPOSURE]

          if (!state.annotationsByType[con.EXPOSURES][data[con.PREPAY_EXPOSURE][con.ID]])
            state.annotationsByType[con.EXPOSURES][data[con.PREPAY_EXPOSURE][con.ID]] = []
          state.annotationsByType[con.EXPOSURES][data[con.PREPAY_EXPOSURE][con.ID]].push(data[con.PREPAY_EXPOSURE_ANNOTATION])
        }

        state[con.TRANSACTION_PREPAYED] = con.OK;
        state[con.INTERNAL_TRANSACTION_CHANGE] += 1;
        createNotification("Exposición prepagada correctamente", con.NOTIFICATION_TIME_SHORT);
      })
      .addCase(prepayExposure.rejected, (state, action) => {
        state[con.TRANSACTION_PREPAYED] = con.ERROR;
        createError(action.payload);
      })

      // ================= FETCH =================
      .addCase(fetchMarketAlerts.pending, (state) => {
        state.market_alerts_status = con.LOADING;
      })
      .addCase(fetchMarketAlerts.fulfilled, (state, action) => {
        state[con.MARKET_ALERTS] = action.payload;
        state.market_alerts_status = con.OK;
      })
      .addCase(fetchMarketAlerts.rejected, (state, action) => {
        state.market_alerts_status = con.ERROR;
        createError(action.payload);
      })

      .addCase(addAlert.fulfilled, (state, action) => {
        state[con.MARKET_ALERTS].push(action.payload);
        createNotification("Alerta creada correctamente", con.NOTIFICATION_TIME_SHORT);
      })
      .addCase(addAlert.rejected, (state, action) => {
        createError(action.payload);
      })

      .addCase(deleteAlert.fulfilled, (state, action) => {
        const alertId = action.payload;
        state[con.MARKET_ALERTS] = state[con.MARKET_ALERTS].filter(
          alert => alert.id !== alertId
        );
        createNotification("Alerta eliminada correctamente", con.NOTIFICATION_TIME_SHORT);
      })
       .addCase(deleteAlert.rejected, (state, action) => {
        createError(action.payload);
      })
  },
});

export const {
   resetTransactions,
   setTransactions,
   firstPage,
   previousPage,
   nextPage,
   finalPage,
   setTotalTransactionsDisplayed
} = transactionsSlice.actions;

export default transactionsSlice.reducer;